import { ReactElement, useEffect, useRef, useState } from "react";
import {
  ListItemType,
  Term,
} from "../tag-input-with-autocomplete/tag-input-with-autocomplete.types";
import { ChangeMethodType } from "./tag-input.types";
import { isOfStringType } from "../tag-input-with-autocomplete/tag-input-with-autocomplete.util";

export function isOfTermType(arg: string | Term): arg is Term {
  return (arg as Term).value !== undefined;
}

/**
 * Listen for clicks that occur somewhere in the document, outside of the component itself
 */
export const useOutsideClick = (
  callback: Function,
  refs: {
    containerRef: React.RefObject<HTMLDivElement>;
    suggestionBoxRef: React.RefObject<HTMLUListElement>;
  }
): void => {
  function handleClickOutside(e: MouseEvent): void {
    const { current: componentWrapper } = refs.containerRef;
    // field wrapper around input
    const innerComponentWrapper = componentWrapper?.children[0];

    // note: on Tag Input (without autocomplete), suggestionBox ref is falsy (null) by default
    if (
      !innerComponentWrapper?.contains(e.target as Node) ||
      (refs.suggestionBoxRef?.current !== null &&
        !refs.suggestionBoxRef?.current?.contains(e.target as Node))
    ) {
      callback();
    }
  }
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  });
};

/**
 * Compare if event target is a tag item
 */
export const isTargetTagItem = (
  tags: HTMLLIElement[],
  target: EventTarget & Element
): boolean => {
  return tags.some((tag) => tag === (target as Node));
};

/**
 * Extract value if item prop is a Term (object), and pass value to onChange in Controlled Tags With Autocomplete component
 */
export const handleSelection = (
  event: ListItemType,
  item: Term | string,
  callback:
    | ((
        event: ListItemType | ChangeMethodType,
        value: string | Term,
        removeTag?: number | undefined
      ) => void)
    | undefined
    | void
): void => {
  const trimItem = isOfTermType(item)
    ? item.value
    : event?.currentTarget?.innerText;
  callback?.(event, trimItem);
};

export const useFilterByText = (
  items: string[] | Term[],
  value: string
): string[] | Term[] => {
  const [filteredList, setFilteredList] = useState<string[] | Term[]>(items);
  const itemsRef = useRef(items);
  const listItemsRef = itemsRef.current;

  useEffect(() => {
    const formatValue = value.toLowerCase();
    if (!isOfStringType(listItemsRef)) {
      const matchTerms = (listItemsRef as Term[]).filter((item) => {
        let isMatch = false;
        for (const prop in item) {
          // extracting the value if item.key is a ReactNode
          const extractItem =
            typeof item[prop] === "object"
              ? (item[prop] as ReactElement)?.props?.children
              : item[prop];

          if (
            prop !== "metadata" &&
            extractItem.toLowerCase().includes(formatValue)
          )
            isMatch = true;
        }
        return isMatch && item;
      });
      setFilteredList(matchTerms as Term[]);
    } else {
      setFilteredList(
        () =>
          (listItemsRef as string[]).filter((item: string) => {
            return item.toLowerCase().includes(formatValue);
          }) as string[]
      );
    }
  }, [listItemsRef, value]);
  return filteredList;
};
